import { Box, Stack, Typography, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";

export const quietButtonSx = {
  textTransform: "none",
  borderRadius: 2,
} as const;

export const primaryActionSx = {
  textTransform: "none",
  borderRadius: 2,
  px: 3,
  minHeight: 42,
} as const;

export const dialogTabSx = {
  borderBottom: 1,
  borderColor: "divider",
  minHeight: 44,
  mb: 2.5,
  "& .MuiTab-root": {
    textTransform: "none",
    minHeight: 44,
    fontWeight: 600,
  },
} as const;

export const surfaceCardSx = {
  p: 2,
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;

export const toggleGroupSx = {
  flexWrap: "wrap",
  "& .MuiToggleButton-root": {
    borderRadius: "1rem",
    textTransform: "none",
    px: 1.5,
    py: 0.5,
  },
} as const;

export function FormSection({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: 2.5,
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        sx={{ mb: 2 }}
      >
        <Stack gap={0.5} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {description ? (
            <Typography color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.5 }}>
              {description}
            </Typography>
          ) : null}
        </Stack>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

export function ChoiceTile({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        appearance: "none",
        font: "inherit",
        color: "inherit",
        textAlign: "start",
        flex: 1,
        minWidth: 120,
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        border: "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: (theme: Theme) =>
          selected ? alpha(theme.palette.primary.main, 0.08) : "background.paper",
        cursor: "pointer",
        fontWeight: selected ? 700 : 500,
        fontSize: 14,
      }}
    >
      {label}
    </Box>
  );
}

export function selectedCardSx(selected: boolean): SxProps<Theme> {
  return {
    p: 2,
    borderRadius: 2.5,
    border: "1px solid",
    borderColor: selected ? "primary.main" : "divider",
    bgcolor: (theme) =>
      selected ? alpha(theme.palette.primary.main, 0.04) : "background.paper",
    cursor: "pointer",
    transition: "border-color 120ms ease, background-color 120ms ease",
    "&:hover": {
      borderColor: selected ? "primary.main" : "text.disabled",
    },
  };
}
